import type {
  CollectionStateSlice,
  CollectionActions,
  Reference,
  Ordering,
} from '@freelancer/datastore/core';
import {
  OrderByDirection,
  mergeDocuments,
  mergeWebsocketDocuments,
  removeDocumentById,
  transformIntoDocuments,
} from '@freelancer/datastore/core';
import { isDefined } from '@freelancer/utils';
import type { ServiceOfferingsCollection } from '../service-offerings';
import { transformServiceOfferingAttachments } from './service-offering-attachments.transformers';
import type { ServiceOfferingAttachmentsCollection } from './service-offering-attachments.types';

export function serviceOfferingAttachmentsReducer(
  state: CollectionStateSlice<ServiceOfferingAttachmentsCollection> = {},
  action:
    | CollectionActions<ServiceOfferingAttachmentsCollection>
    | CollectionActions<ServiceOfferingsCollection>,
): CollectionStateSlice<ServiceOfferingAttachmentsCollection> {
  switch (action.type) {
    case 'API_FETCH_SUCCESS': {
      if (action.payload.type === 'serviceOfferingAttachments') {
        const { result, ref, order } = action.payload;

        return mergeDocuments<ServiceOfferingAttachmentsCollection>(
          state,
          transformIntoDocuments(
            result.service_offering_attachments,
            transformServiceOfferingAttachments,
          ),
          order,
          ref,
        );
      }

      if (action.payload.type === 'serviceOfferings') {
        const { result } = action.payload;

        const ref: Reference<ServiceOfferingAttachmentsCollection> = {
          path: {
            collection: 'serviceOfferingsAttachments',
            authUid: action.payload.ref.path.authUid,
          },
        };
        const order: Ordering<ServiceOfferingAttachmentsCollection> = [
          { field: 'id', direction: OrderByDirection.ASC },
        ];

        return mergeDocuments<ServiceOfferingAttachmentsCollection>(
          state,
          transformIntoDocuments(
            result.service_offerings
              .flatMap(serviceOffering => serviceOffering.attachments)
              .filter(isDefined),
            transformServiceOfferingAttachments,
            ref.path.authUid,
          ),
          order,
          ref,
        );
      }
      return state;
    }

    case 'API_PUSH_SUCCESS': {
      if (action.payload.type === 'serviceOfferingAttachments') {
        const { result, ref } = action.payload;

        return mergeWebsocketDocuments<ServiceOfferingAttachmentsCollection>(
          state,
          transformIntoDocuments(
            result.service_offering_attachments,
            transformServiceOfferingAttachments,
          ),
          ref,
        );
      }
      return state;
    }

    case 'API_UPDATE_SUCCESS': {
      if (action.payload.type === 'serviceOfferingAttachments') {
        const { result, ref } = action.payload;

        return mergeWebsocketDocuments<ServiceOfferingAttachmentsCollection>(
          state,
          transformIntoDocuments(
            result.service_offering_attachments,
            transformServiceOfferingAttachments,
          ),
          ref,
        );
      }

      if (action.payload.type === 'serviceOfferings') {
        const { result } = action.payload;

        if (!isServiceOfferingsAttachmentUpdateResult(result)) {
          return state;
        }

        const ref: Reference<ServiceOfferingAttachmentsCollection> = {
          path: {
            collection: 'serviceOfferingAttachments',
            authUid: action.payload.ref.path.authUid,
          },
        };

        return mergeWebsocketDocuments<ServiceOfferingAttachmentsCollection>(
          state,
          transformIntoDocuments(
            result.service_offering_attachments,
            transformServiceOfferingAttachments,
          ),
          ref,
        );
      }

      return state;
    }

    case 'API_DELETE_SUCCESS': {
      if (action.payload.type === 'serviceOfferingAttachments') {
        const { originalDocument, ref } = action.payload;
        return removeDocumentById<ServiceOfferingAttachmentsCollection>(
          ref,
          state,
          originalDocument.id,
        );
      }

      return state;
    }

    default: {
      return state;
    }
  }
}

function isServiceOfferingsAttachmentUpdateResult(
  result:
    | ServiceOfferingAttachmentsCollection['Backend']['Update']['ReturnType']
    | ServiceOfferingsCollection['Backend']['Update']['ReturnType'],
): result is ServiceOfferingAttachmentsCollection['Backend']['Update']['ReturnType'] {
  return (
    (
      result as ServiceOfferingAttachmentsCollection['Backend']['Update']['ReturnType']
    ).service_offering_attachments !== undefined
  );
}
